import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  approveKpiAccess,
  fetchKpiAccessRequests,
  fetchKpiLockState,
  rejectKpiAccess,
  requestKpiAccess,
} from "./kpiLockApi";
import { buildAccessRequests, summariseLocks } from "./helpers";

/**
 * Tracks which KPIs are locked by a passed due date, and raises access requests.
 *
 * Lock state is read from the server rather than derived here: the same service
 * decides it and refuses late saves, so the badge on a row always agrees with
 * what the API will do.
 * @param {object} params the financial year, KPIs, locations and questionnaire.
 * @returns {object} the summarised locks, pending requests, and a request action.
 */
export function useKpiLocks({
  financialYearId,
  questionIds,
  locationIds,
  questionnaireType = "QUANT",
}) {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState(null);
  const fetchId = useRef(0);

  const idKey = (questionIds ?? []).join(",");
  const locationKey = (locationIds ?? []).join(",");

  const load = useCallback(async () => {
    if (!financialYearId || !idKey) {
      setRows([]);
      return;
    }
    const id = ++fetchId.current;
    setError(null);

    try {
      const [lockRes, requestRes] = await Promise.all([
        fetchKpiLockState({
          financialYearId,
          questionIds: idKey.split(","),
          locationIds: locationKey ? locationKey.split(",") : [],
          questionnaireType,
        }),
        fetchKpiAccessRequests(financialYearId),
      ]);
      if (id !== fetchId.current) return;

      if (lockRes?.isSuccess) setRows(lockRes.data ?? []);
      else setError("Could not check which KPIs are locked");

      if (requestRes?.isSuccess) setRequests(requestRes.data ?? []);
    } catch {
      if (id !== fetchId.current) return;
      setError("Could not check which KPIs are locked");
    }
  }, [financialYearId, idKey, locationKey, questionnaireType]);

  useEffect(() => {
    load();
    /** Bumping the fetch id on unmount makes any in-flight response a no-op. */
    return () => {
      fetchId.current += 1;
    };
  }, [load]);

  const locks = useMemo(() => summariseLocks(rows), [rows]);

  /**
   * Requests that a KPI be reopened at the locations where it is locked.
   * @param {object} params the KPI, its lock state and the period in view.
   * @returns {Promise<{ok: boolean, message?: string}>} the outcome.
   */
  const requestAccess = useCallback(
    async ({ kpiId, lock, fromDate, toDate, reason }) => {
      const res = await requestKpiAccess({
        financialYearId,
        questionnaireType,
        requests: buildAccessRequests({ kpiId, lock }),
        periodFrom: fromDate || undefined,
        periodTo: toDate || undefined,
        reason: reason || undefined,
      });
      if (res?.isSuccess) await load();
      return { ok: !!res?.isSuccess, message: res?.message };
    },
    [financialYearId, questionnaireType, load],
  );

  /**
   * Grants an extension on a KPI's outstanding requests.
   * @param {number[]} ids the requests to approve.
   * @param {number} increasedDays how many days to reopen entry for.
   * @returns {Promise<{ok: boolean, message?: string}>} the outcome.
   */
  const approveAccess = useCallback(
    async (ids, increasedDays, locationIds) => {
      const res = await approveKpiAccess(ids, increasedDays, locationIds);
      if (res?.isSuccess) await load();
      return { ok: !!res?.isSuccess, message: res?.message };
    },
    [load],
  );

  /**
   * Declines a KPI's outstanding requests, leaving it locked.
   * @param {number[]} ids the requests to reject.
   * @returns {Promise<{ok: boolean, message?: string}>} the outcome.
   */
  const rejectAccess = useCallback(
    async (ids, locationIds) => {
      const res = await rejectKpiAccess(ids, locationIds);
      if (res?.isSuccess) await load();
      return { ok: !!res?.isSuccess, message: res?.message };
    },
    [load],
  );

  return {
    locks,
    requests,
    error,
    requestAccess,
    approveAccess,
    rejectAccess,
    refresh: load,
  };
}
